import React, { useState, useEffect } from "react";
import { 
  X, 
  Download, 
  Trash2, 
  Save, 
  Loader2, 
  Folder, 
  Tag, 
  Calendar, 
  User, 
  HardDrive, 
  FileText,
  ExternalLink
} from "lucide-react";

export default function DocumentDetailsModal({
  isOpen,
  onClose,
  document: docItem,
  allFolders = [],
  onUpdate,
  onDelete,
  onDownload,
  loading
}) {
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isOpen && docItem) {
      setTitle(docItem.title || docItem.originalFileName || "");
      setFolderId(docItem.folderId || "");
      setTagInput("");
      setTags(Array.isArray(docItem.tags) ? [...docItem.tags] : []);
      setDescription(docItem.description || "");
      setError("");
      setSaving(false);
      setDeleting(false);
    }
  }, [isOpen, docItem]);

  if (!isOpen || !docItem) return null;

  const handleAddTag = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const val = tagInput.trim().replace(/^#/, "");
      if (val && !tags.includes(val)) {
        setTags([...tags, val]);
      }
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError("Document title cannot be empty.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await onUpdate(docItem.id, {
        title: title.trim(),
        folderId: folderId || null,
        tags,
        description: description.trim()
      });
      onClose();
    } catch (err) {
      console.error("Error updating document:", err);
      setError(err.message || "Failed to update document.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to permanently delete "${docItem.title}"?`)) return;
    setDeleting(true);
    try {
      await onDelete(docItem.id, docItem.storagePath);
      onClose();
    } catch (err) {
      console.error("Error deleting document:", err);
      setError(err.message || "Failed to delete document.");
      setDeleting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content glass-card animate-slide-up" style={{ maxWidth: "560px" }}>
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <div 
              style={{ 
                width: "36px", 
                height: "36px", 
                borderRadius: "8px", 
                backgroundColor: "var(--primary-light)", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                color: "var(--primary)"
              }}
            >
              <FileText size={20} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700, color: "var(--text-primary)" }}>
                Document Details
              </h3>
              <span className="text-secondary" style={{ fontSize: "0.75rem" }}>
                {docItem.originalFileName}
              </span>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Metadata Summary Banner */}
        <div 
          style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(3, 1fr)", 
            gap: "0.75rem", 
            padding: "0.85rem", 
            backgroundColor: "var(--bg-input)", 
            borderRadius: "8px", 
            border: "1px solid var(--border-color)",
            marginTop: "1.25rem"
          }}
        >
          <div>
            <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "0.25rem" }}>
              <HardDrive size={12} /> Size
            </span>
            <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-primary)", marginTop: "0.15rem" }}>
              {formatFileSize(docItem.fileSize)}
            </div>
          </div>
          <div>
            <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "0.25rem" }}>
              <Calendar size={12} /> Uploaded
            </span>
            <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-primary)", marginTop: "0.15rem" }}>
              {docItem.uploadedAt ? new Date(docItem.uploadedAt).toLocaleDateString() : "N/A"}
            </div>
          </div>
          <div>
            <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "0.25rem" }}>
              <User size={12} /> Author
            </span>
            <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-primary)", marginTop: "0.15rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {docItem.uploadedBy || "Team"}
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} style={{ marginTop: "1.25rem" }}>
          <div className="form-group">
            <label className="form-label" htmlFor="editDocTitle">Document Title</label>
            <input
              id="editDocTitle"
              type="text"
              required
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="grid-2" style={{ gap: "1rem", marginTop: "1rem" }}>
            <div className="form-group">
              <label className="form-label" htmlFor="editDocFolder">Folder Location</label>
              <select
                id="editDocFolder"
                className="form-input"
                value={folderId}
                onChange={(e) => setFolderId(e.target.value)}
              >
                <option value="">📁 Root / Top Level</option>
                {allFolders.map((f) => (
                  <option key={f.id} value={f.id}>
                    📁 {f.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="editTagInput">Tags / Keywords</label>
              <input
                id="editTagInput"
                type="text"
                placeholder="Type and press Enter..."
                className="form-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
              />
            </div>
          </div>

          {tags.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.5rem" }}>
              {tags.map((t) => (
                <span
                  key={t}
                  className="sku-badge"
                  style={{
                    fontSize: "0.75rem",
                    padding: "0.2rem 0.5rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.3rem"
                  }}
                >
                  #{t}
                  <X
                    size={12}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRemoveTag(t)}
                  />
                </span>
              ))}
            </div>
          )}

          <div className="form-group" style={{ marginTop: "1rem" }}>
            <label className="form-label" htmlFor="editDocDescription">Description</label>
            <textarea
              id="editDocDescription"
              rows={2}
              className="form-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {error && <div className="alert alert-danger" style={{ marginTop: "1rem" }}>{error}</div>}

          <div className="modal-footer" style={{ marginTop: "1.75rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => onDownload(docItem)}
                style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
              >
                <Download size={15} /> Download
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleDelete}
                disabled={deleting}
                style={{ color: "var(--danger)", borderColor: "var(--border-color)", display: "flex", alignItems: "center", gap: "0.4rem" }}
              >
                {deleting ? <Loader2 className="animate-spin" size={15} /> : <Trash2 size={15} />} Delete
              </button>
            </div>

            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="animate-spin" size={15} style={{ marginRight: "0.4rem" }} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={15} style={{ marginRight: "0.4rem" }} /> Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
